import { Injectable, signal, inject } from '@angular/core';
import { ChatMessage, ChatRole, IDynamicFlowTask, SimpleAgentTask } from '../models/agent.models';
import { TOAST_ALERTS_TOKEN, ToastAlertsAbstractService, EModelQuality } from '@dataclouder/ngx-core';
import { DefaultAgentCardsService } from './default-agent-service';
import { ContextType } from '../models/agent.models';
import { DynamicFlowService } from './dynamic-flow.service';
import { ContextEngineService } from './context-engine.service';
import { DialogService } from 'primeng/dynamicdialog';
import { FeedbackEvaluationComponent } from '../components/feedback-evaluation/feedback-evaluation.component';
import { performanceAnalysisPromptTemplate, PerformanceEvaluation } from '../prompts/evaluation-performance-prompt';
import { LoadingBarService } from '@dataclouder/ngx-core';

class AnalysisResult {
  score: number;
  level: 'Beginner' | 'Intermediate' | 'Advanced' | 'Native-like';
  feedback: string;
  improvement_suggestions: string;
}

export interface IEvaluationResponse {
  text: string;
  score: number;
}

@Injectable({
  providedIn: 'root',
})
export class EvaluationService {
  // Services
  private defaultAgentCardService = inject(DefaultAgentCardsService);
  private toastService = inject<ToastAlertsAbstractService>(TOAST_ALERTS_TOKEN);
  private dynamicFlowService = inject(DynamicFlowService);
  private contextEngineService = inject(ContextEngineService);
  private dialogService = inject(DialogService);
  private loadingBarService = inject(LoadingBarService);

  // Signals
  public scoreSignal = signal<number>(0); // Only Score
  private evaluationResultSignal = signal<IEvaluationResponse | null>(null); // Not sure if ill give some use

  async evaluateGoal(goalTask: IDynamicFlowTask): Promise<any> {
    if (!goalTask.task) {
      goalTask.task = 'Evaluate this conversation the goal is keep the conversation on topic consistency.';
    }

    const evaluator: SimpleAgentTask = {
      task: goalTask.task,
      expectedResponseType:
        'Response should be in next JSON format: {"score": number, "text": string}, score is a number between 0 and 3, text is concrete feedback in less than 25 words.',
      model: goalTask.model || { quality: EModelQuality.FAST },
    };

    const evaluationResult = await this.evaluateConversation(evaluator, ContextType.LastMessage);

    this.updateScore(evaluationResult.score || 0);

    console.log(' ♦️ Goal Evaluation Result:', evaluationResult, this.scoreSignal());

    // Evaluation can return evything check i can standarize this.
    this.toastService.info({
      title: 'Score ' + evaluationResult?.text,
      subtitle: `Added ${evaluationResult?.score} points`,
    });
    return evaluationResult;
  }

  async evaluateConversation(task: SimpleAgentTask, contextType: ContextType = ContextType.AllConversation): Promise<IEvaluationResponse> {
    // Format conversation for evaluation
    const conversationMessagesString = this.contextEngineService.getConversationContext(contextType);
    const userDataString = this.contextEngineService.getUserData();

    // Create evaluation prompt
    let instructions = `
Please replay to this task:
    ${task.task}
<UserData>
    ${userDataString}
</UserData>
This is the conversation history:
<Context>
    ${conversationMessagesString}
</Context>

    `;

    if (task.expectedResponseType) {
      instructions += `\nExpected response type: ${task.expectedResponseType}`;
    } else {
      instructions += `\nExpected response type: {"score": number, "text": string}`;
    }

    // Send evaluation request
    const evaluationMessages: ChatMessage[] = [{ content: instructions, role: ChatRole.User }];
    // Not sure what strategy use, if works types should be definied in definitions, generarally response will be score, and feedback.
    const response = await this.defaultAgentCardService.callChatCompletion({
      messages: evaluationMessages,
      returnJson: true,
      model: task.model || { quality: EModelQuality.FAST },
    });

    // Extract JSON from response
    const jsonData: any = response.content;
    jsonData.score = jsonData.score * 10;
    this.evaluationResultSignal.set(jsonData);

    return jsonData;
  }

  /**
   * Diference with evaluateConversation is task is already processed here.
   * Evaluates a conversation context based on a specific task and attaches the result to a designated message.
   * @param agentTask The task definition for the evaluation.
   * @param messageToUpdate The specific ChatMessage object (user or assistant) to attach the evaluation results to.
   * @param contextType Determines which part of the conversation history to send as context (default: AllConversation).
   * @returns The JSON result of the evaluation.
   */
  async evaluateWithTask(agentTask: IDynamicFlowTask): Promise<any> {
    // You Already Generate Task with all data needed..
    const requestMessages: ChatMessage[] = [{ role: ChatRole.User, content: agentTask.task }];

    try {
      const response = await this.defaultAgentCardService.callChatCompletion({ messages: requestMessages, model: agentTask.model });
      // let jsonData = extractJsonFromResponse(response.content);
      let jsonData: IEvaluationResponse = response.content as IEvaluationResponse;
      if (typeof jsonData === 'string') {
        return { text: jsonData };
      }
      return jsonData;
    } catch (error: any) {
      console.error('Error during evaluateWithTask:', error);
      return null;
    }
  }

  public updateScore(additionalScore: number): void {
    // Aqui tengo que verificar que se cumple condición de goal!
    const previuesScore = this.scoreSignal();
    const newScore = previuesScore + additionalScore;

    // How to add the conditions dynamically?

    this.dynamicFlowService.checkDynamicConditions(previuesScore, newScore);

    if (newScore >= 100) {
      this.scoreSignal.set(100);
      this.analylizePerformance();
    } else {
      console.log('Score updated to:', newScore);
      this.scoreSignal.set(newScore);
    }
  }

  public setScore(newScore: number): void {
    this.dynamicFlowService.checkDynamicConditions(this.scoreSignal(), newScore);
    this.scoreSignal.set(newScore);

    if (newScore >= 100) {
      this.scoreSignal.set(100);
      this.analylizePerformance();
    } else {
      this.scoreSignal.set(newScore);
    }
  }

  public resetScore(): void {
    this.scoreSignal.set(0);
  }

  public async analylizePerformance(): Promise<any> {
    console.log('Analylizing performance...');
    const conversationMessages = this.contextEngineService.getContextMessages(ContextType.AllConversation);
    if (conversationMessages.length < 6) {
      return;
    }
    const conversationContext = this.contextEngineService.getConversationContext(ContextType.AllConversation);

    // TODO change the english!
    let prompt = performanceAnalysisPromptTemplate('English');
    prompt += `\n\nHere the user conversation: <Context>${conversationContext}</Context>`;

    this.loadingBarService.showIndeterminate();

    const response = await this.defaultAgentCardService.callChatCompletion({
      messages: [{ role: ChatRole.User, content: prompt }],
      returnJson: true,
    });

    this.loadingBarService.successAndHide();
    this.openFeedbackEvaluation(response.content as PerformanceEvaluation);

    console.log('Performance analysis response:', response);
    return response;
  }

  public openFeedbackEvaluation(data?: PerformanceEvaluation): void {
    this.dialogService.open(FeedbackEvaluationComponent, {
      data,
      header: 'Evaluation Feedback',
      width: '70%',
      contentStyle: { height: '80vh', overflow: 'auto' },
      baseZIndex: 10000,
      closable: true,
    });
  }
}
